import { stratify } from "d3";
import { ITeam } from "@gtmhub/teams";
import { IdMap } from "@gtmhub/util";
import { TreeDataNode } from "./tree-models";

/**
 * createTeamsTree
 * Converts GTMHUB teams data to a D3-compatible hierarchy structure. This function was originally part of the first Organization Tree.
 * The code was extracted without changes.
 *
 * Note: both TreeDataNode and HierarchyNode<ITeam> match the returned structure, so typecasting to either of them is safe.
 */
export function createTeamsTree(teams: ITeam[], teamsIdMap: IdMap<ITeam>): TreeDataNode {
  const fakeRoot: ITeam = { id: "root" };

  const buildTree = stratify<ITeam>()
    .id((d) => d.id)
    .parentId((d) => {
      if (d.id === fakeRoot.id) {
        return null;
      }

      return !d.parentId || !teamsIdMap[d.parentId] ? fakeRoot.id : d.parentId;
    });

  return buildTree([...teams, fakeRoot]) as TreeDataNode; // HierarchyNode is fully compatible with TreeDataNode
}

/**
 * fixIncorrectMembersOrder
 * The backend does not guarentee the order of the members in ".membersInfo", but it does
 * send the order they should be displayed in in ".members". This utility function corrects the order
 * of members in ".membersInfo", so that the rest of the code can use them smoothly.
 *
 * IMPORTANT: For performance reasons input ITeam is modified directly, rather than returning a copy.
 *
 * @param {ITeam} team
 * @return {void}
 */
export function fixIncorrectMembersOrder(team: ITeam): void {
  if (!team.members || !team.membersInfo) {
    return;
  }

  team.membersInfo = team.members.map((memberId) => team.membersInfo.find((m) => m?.id === memberId)).filter((m) => !!m);
}
